import { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Button,
  cn,
  Icon,
  Skeleton,
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@nexus/ui";
import { FailurePanel } from "@/components/FailurePanel";
import type { EventActionItem, EventJobItem } from "@/features/events/api/events.api";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { eventsByCorrelationQueryOptions } from "@/lib/queries";
import { selectActionFlowEvent } from "../lib/event-action-display";
import { stripTopicPrefix } from "../lib/event-row-display";
import { getSentryTraceUrl } from "../lib/sentry-trace-link";
import { formatPreciseDurationMs, getEventEndToEndMs } from "../lib/event-timing";
import { EVENT_DETAIL_ROW_GRID, EventDetailSection } from "./shared/EventDetailSection";
import { EventJsonSection } from "./shared/EventJsonSection";
import { EventStatusBadge } from "./shared/EventStatusBadge";
import { EventTimingSection } from "./shared/EventTimingSection";

interface EventActionDetailDrawerProps {
  action: EventActionItem | null;
  now: number;
  stuckAfterMs: number;
  windowHours: number;
  onClose: () => void;
  onShowEvents: (action: EventActionItem) => void;
  onRepublish?: (event: EventJobItem) => void;
}

const EMPTY_VALUE = "—";
const COPIED_RESET_MS = 2_000;
const ACTION_EVENTS_POLL_MS = 3_000;

function CopyValueButton({ value, label }: { value: string; label: string }) {
  const { t } = useTranslation();
  const { copied, copy } = useCopyToClipboard({ resetMs: COPIED_RESET_MS });

  return (
    <Button
      variant="ghost"
      size="icon-xs"
      className={cn(
        "shrink-0 rounded-sm transition-opacity",
        "min-h-11 min-w-11",
        "[@media(hover:hover)_and_(pointer:fine)]:size-5",
        "[@media(hover:hover)_and_(pointer:fine)]:min-h-0",
        "[@media(hover:hover)_and_(pointer:fine)]:min-w-0",
        !copied && "[@media(hover:hover)_and_(pointer:fine)]:opacity-0",
        "group-hover/field:opacity-100 focus-visible:opacity-100",
      )}
      onClick={() => void copy(value)}
      aria-label={
        copied
          ? t("admin.events.drawer.copied", "Copied")
          : t("admin.events.drawer.copyField", "Copy {{field}}", { field: label })
      }
    >
      <Icon
        name={copied ? "check" : "copy"}
        size="xs"
        colorVar={copied ? "--success" : undefined}
        aria-hidden
      />
    </Button>
  );
}

function IdentityRow({
  label,
  value,
  mono = false,
  copyable = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
  copyable?: boolean;
}) {
  const isEmpty = value === EMPTY_VALUE;

  return (
    <div className={cn("group/field", EVENT_DETAIL_ROW_GRID)}>
      <dt className="text-xs leading-5 text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          "leading-5",
          mono ? "break-all font-mono text-[11px]" : "text-xs",
          isEmpty && "text-muted-foreground",
        )}
      >
        {value}
      </dd>
      <dd>{copyable && !isEmpty ? <CopyValueButton value={value} label={label} /> : null}</dd>
    </div>
  );
}

export function EventActionDetailDrawer({
  action,
  now,
  stuckAfterMs,
  windowHours,
  onClose,
  onShowEvents,
  onRepublish,
}: EventActionDetailDrawerProps) {
  if (!action) return null;

  return (
    <ActionDrawerContent
      key={action.correlationId}
      action={action}
      now={now}
      stuckAfterMs={stuckAfterMs}
      windowHours={windowHours}
      onClose={onClose}
      onShowEvents={onShowEvents}
      onRepublish={onRepublish}
    />
  );
}

function ActionDrawerContent({
  action,
  now,
  stuckAfterMs,
  windowHours,
  onClose,
  onShowEvents,
  onRepublish,
}: {
  action: EventActionItem;
  now: number;
  stuckAfterMs: number;
  windowHours: number;
  onClose: () => void;
  onShowEvents: (action: EventActionItem) => void;
  onRepublish?: (event: EventJobItem) => void;
}) {
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    ...eventsByCorrelationQueryOptions(action.correlationId, windowHours),
    refetchInterval: (query) => {
      const items = query.state.data?.items;
      if (!items) return false;
      return items.some((event) => event.status === "PENDING" || event.status === "PROCESSING")
        ? ACTION_EVENTS_POLL_MS
        : false;
    },
  });

  const events = useMemo(() => {
    const items = data?.items ?? [];
    return [...items].sort(
      (left, right) =>
        left.publishedAt.localeCompare(right.publishedAt) || left.id.localeCompare(right.id),
    );
  }, [data?.items]);

  const selectedEvent = selectActionFlowEvent(events, selectedEventId);
  const failedEvent = events.find((event) => event.status === "FAILED");
  const traceUrl = getSentryTraceUrl(action.sentryTraceId);

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        ref={contentRef}
        aria-describedby={undefined}
        className="focus:outline-none"
        onOpenAutoFocus={(focusEvent) => {
          focusEvent.preventDefault();
          contentRef.current?.focus();
        }}
      >
        <SheetHeader className="gap-0 pr-12">
          <div className="flex flex-wrap items-center gap-2.5">
            <SheetTitle>{action.eventType}</SheetTitle>
            <EventStatusBadge status={action.status} />
          </div>
          <div className="mt-1 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
            {stripTopicPrefix(action.topic)}
          </div>
        </SheetHeader>

        <SheetBody className="flex flex-col gap-6">
          <FailurePanel
            key={`${action.correlationId}-error`}
            code={failedEvent?.errorCode}
            message={failedEvent?.errorMessage ?? null}
          />

          <EventDetailSection label={t("admin.events.drawer.identity", "Identity")}>
            <dl className="flex flex-col gap-1.5">
              <IdentityRow
                label={t("admin.events.drawer.correlationId", "Correlation ID")}
                value={action.correlationId}
                mono
                copyable
              />
              <IdentityRow
                label={t("admin.events.drawer.tenant", "Tenant")}
                value={action.tenantId ?? EMPTY_VALUE}
                mono
                copyable
              />
              <IdentityRow
                label={t("admin.events.drawer.user", "User")}
                value={action.actorUserName ?? action.actorUserId ?? EMPTY_VALUE}
              />
            </dl>

            {traceUrl ? (
              <a
                href={traceUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-[11px] text-link hover:underline"
              >
                {t("admin.events.drawer.sentryTrace", "Open the Sentry trace")}
                <Icon name="external-link" size="xs" aria-hidden />
              </a>
            ) : null}
          </EventDetailSection>

          <EventDetailSection
            label={t("admin.events.actions.drawer.flow", "Events")}
            aside={
              events.length > 0 ? (
                <span className="text-[11px] tabular-nums text-muted-foreground">
                  {t("admin.events.actions.drawer.flowCount", "{{count}} events", {
                    count: events.length,
                  })}
                  {action.failedCount > 0
                    ? ` ${t("admin.events.actions.table.failedCount", "({{count}} failed)", {
                        count: action.failedCount,
                      })}`
                    : null}
                </span>
              ) : null
            }
          >
            {isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : isError ? (
              <div className="flex flex-col items-start gap-2">
                <p className="text-sm text-muted-foreground">
                  {t("admin.events.actions.drawer.flowLoadError", "Failed to load events.")}
                </p>
                <Button variant="outline" size="sm" onClick={() => void refetch()}>
                  {t("admin.events.retry", "Retry")}
                </Button>
              </div>
            ) : events.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("admin.events.actions.drawer.flowEmpty", "No events recorded for this action.")}
              </p>
            ) : (
              <ol className="flex flex-col">
                {events.map((event, index) => {
                  const isSelected = selectedEvent?.id === event.id;
                  const endToEndMs = getEventEndToEndMs(event);
                  const isFailed = event.status === "FAILED";

                  return (
                    <li key={event.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedEventId(event.id)}
                        aria-current={isSelected}
                        aria-label={t(
                          "admin.events.actions.drawer.openEvent",
                          "Open {{eventType}}",
                          { eventType: event.eventType },
                        )}
                        className={cn(
                          "flex w-full items-center gap-3 rounded-md px-3 py-2 text-left",
                          "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
                          isSelected && "bg-muted",
                          isFailed && "bg-error-subtle",
                        )}
                      >
                        <span className="w-4 shrink-0 font-mono text-[10px] tabular-nums text-muted-foreground">
                          {index + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-xs" title={event.eventType}>
                            {event.eventType}
                          </div>
                          <div className="truncate font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
                            {stripTopicPrefix(event.topic)}
                          </div>
                        </div>
                        <EventStatusBadge status={event.status} />
                        <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground">
                          {endToEndMs != null ? formatPreciseDurationMs(endToEndMs) : EMPTY_VALUE}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            )}
          </EventDetailSection>

          {selectedEvent ? (
            <>
              <EventTimingSection event={selectedEvent} now={now} stuckAfterMs={stuckAfterMs} />
              <EventJsonSection
                key={`${selectedEvent.id}-payload`}
                label={t("admin.events.drawer.payload", "Payload")}
                value={selectedEvent.payload}
              />
              <EventJsonSection
                key={`${selectedEvent.id}-attributes`}
                label={t("admin.events.drawer.attributes", "Attributes")}
                value={selectedEvent.attributes}
              />
            </>
          ) : null}
        </SheetBody>

        <SheetFooter className="justify-between px-6">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 rounded-sm text-xs"
            onClick={() => onShowEvents(action)}
          >
            {t("admin.events.actions.drawer.viewInEvents", "Show in events list")}
          </Button>
          <div className="flex items-center gap-2">
            {onRepublish && selectedEvent ? (
              <Button
                variant="outline"
                size="sm"
                className="h-8 rounded-sm text-xs"
                onClick={() => onRepublish(selectedEvent)}
              >
                <Icon name="refresh-cw" size="xs" aria-hidden />
                {t("admin.events.drawer.republish", "Republish")}
              </Button>
            ) : null}
            <SheetClose asChild>
              <Button variant="outline" size="sm" className="h-8 rounded-sm text-xs">
                {t("admin.events.drawer.close", "Close")}
              </Button>
            </SheetClose>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
